import {
  type CustomField,
  type CustomFieldObject,
  getObjectFlags,
} from '@clockwise/clients/custom-fields';
import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCustomFieldDefinitions } from '@/lib/use-custom-field-definitions';

/**
 * Stable hooks for e2e + unit tests. Radix Select is unreliable to drive by
 * role + visible text (the option click doesn't always commit, and the
 * listbox can close across an async gap), so tests address the triggers and
 * options by id instead. Options are keyed by their Select VALUE — the
 * definition id for the field picker, the choice string for the value
 * picker — plus the `__none__` / `__any__` sentinels, so a test never has to
 * know a label to find an option.
 */
export const CUSTOM_FIELD_FILTER_TESTID = {
  fieldTrigger: 'custom-field-filter-field',
  fieldOption: (value: string) => `custom-field-filter-field-option-${value}`,
  valueTrigger: 'custom-field-filter-value',
  valueOption: (value: string) => `custom-field-filter-value-option-${value}`,
  valueInput: 'custom-field-filter-value-input',
} as const;
const TESTID = CUSTOM_FIELD_FILTER_TESTID;

interface CustomFieldFilterProps {
  object: CustomFieldObject;
  /** Selected definition id (UUID). Empty string when no filter is active. */
  fieldId: string;
  /** Selected value. Empty string when no filter is active. */
  value: string;
  /** Called with `(fieldId, value)`. Pass both empty strings to clear. */
  onChange: (fieldId: string, value: string) => void;
  /** Optional test seam — see CustomFieldsEditor for the contract. */
  definitions?: readonly CustomField[];
}

/**
 * Compact list-page filter driven by the `searchable` flag on custom-field
 * definitions. Renders a field picker (only definitions marked searchable on
 * this object appear) + a value control whose shape follows the selected
 * field's type (`select` → dropdown, others → text input). When either side
 * is empty, the parent treats the filter as inactive and drops it from the
 * list query.
 *
 * v1 limit: one active filter at a time. The backend supports multiple in
 * its FindRequest but the flat-params surface only exposes one
 * `customFieldId` / `customFieldValue` pair.
 */
export function CustomFieldFilter({
  object,
  fieldId,
  value,
  onChange,
  definitions: definitionsProp,
}: CustomFieldFilterProps) {
  const query = useCustomFieldDefinitions(object);
  const definitions = definitionsProp ?? query.data;

  // The field the user has picked but not yet given a value. Parents treat a
  // half-set pair as inactive, and the list pages go further: they persist
  // the pair in the URL only when BOTH halves are set, so `onChange(id, '')`
  // comes back as `fieldId=''`. Deriving the picker from the prop alone
  // therefore snapped the field picker back to "No custom-field filter" the
  // moment a field was picked, and the value control never appeared. The
  // picked field is UI state until a value makes it a filter, so it lives
  // here. A non-empty `fieldId` prop always wins and is remembered, so
  // "Any value" (which clears the value) keeps showing the field it cleared.
  const [pendingFieldId, setPendingFieldId] = useState(fieldId);
  if (fieldId && fieldId !== pendingFieldId) setPendingFieldId(fieldId);
  const activeFieldId = fieldId || pendingFieldId;

  const searchable = useMemo(
    () => (definitions ?? []).filter((d) => getObjectFlags(d, object).searchable),
    [definitions, object],
  );

  if (searchable.length === 0) return null;

  const selected = searchable.find((d) => d.id === activeFieldId);
  const choices = selected?.options?.choices ?? [];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={selected?.id ?? '__none__'}
        onValueChange={(v) => {
          if (v === '__none__') {
            setPendingFieldId('');
            onChange('', '');
            return;
          }
          setPendingFieldId(v);
          // Picking a different field always clears the value — old value
          // rarely makes sense against a new field's choices.
          onChange(v, '');
        }}
      >
        <SelectTrigger className="w-[180px]" data-testid={TESTID.fieldTrigger}>
          <SelectValue placeholder="Custom field" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__" data-testid={TESTID.fieldOption('__none__')}>
            No custom-field filter
          </SelectItem>
          {searchable.map((d) => (
            <SelectItem key={d.id} value={d.id} data-testid={TESTID.fieldOption(d.id)}>
              {d.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selected ? (
        selected.fieldType === 'select' || selected.fieldType === 'multi_select' ? (
          <Select
            value={value || '__any__'}
            onValueChange={(v) => onChange(selected.id, v === '__any__' ? '' : v)}
          >
            <SelectTrigger className="w-[180px]" data-testid={TESTID.valueTrigger}>
              <SelectValue placeholder="Any value" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__" data-testid={TESTID.valueOption('__any__')}>
                Any value
              </SelectItem>
              {choices.map((c) => (
                <SelectItem key={c} value={c} data-testid={TESTID.valueOption(c)}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            data-testid={TESTID.valueInput}
            className="w-[200px]"
            placeholder={`Filter by ${selected.label.toLowerCase()}`}
            value={value}
            onChange={(e) => onChange(selected.id, e.target.value)}
          />
        )
      ) : null}
    </div>
  );
}
